import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useInvestigationsStoreApi } from '@/stores/investigations-store-provider'
import type { Investigation } from '@/types/investigation'

/** Detail-page actions for a saved investigation: open-in-graph, toggle
 *  report inclusion, and delete (with confirm dialog state). */
export function useInvestigationActions(inv: Investigation, caseId: string) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const storeApi = useInvestigationsStoreApi()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [togglingReport, setTogglingReport] = useState(false)

  const openInGraph = () => {
    storeApi.getState().setActive(inv.id)
    navigate(`/cases/${caseId}/graph?investigation=${inv.id}`)
  }

  const toggleReport = async () => {
    if (togglingReport) return
    const next = !inv.includedInReport
    setTogglingReport(true)
    try {
      await storeApi.getState().updateById(inv.id, { includedInReport: next })
      toast.success(
        next
          ? t('investigations.page.featured.addedToReport')
          : t('investigations.page.featured.removedFromReport')
      )
    } catch (err) {
      toast.error(
        t('investigations.page.featured.toggleFailed', { message: (err as Error).message })
      )
    } finally {
      setTogglingReport(false)
    }
  }

  const confirmDeleteAction = async () => {
    setDeleting(true)
    try {
      await storeApi.getState().remove(inv.id)
      toast.success(t('investigations.page.delete.success'))
      navigate(`/cases/${caseId}/investigations`)
    } catch (err) {
      toast.error(t('investigations.page.delete.failed', { message: (err as Error).message }))
      setDeleting(false)
    }
  }

  return {
    confirmDelete,
    setConfirmDelete,
    deleting,
    togglingReport,
    openInGraph,
    toggleReport,
    confirmDeleteAction,
  }
}
